import React, { useCallback } from "react";
import { Grid, Typography, Box, Stack, Paper, TextField, Switch, FormControlLabel } from "@mui/material";
import { Psychology } from "@mui/icons-material";
import MultiSelect from "../../../../../../FormFields/CustomSelect/MultiSelect";

const brainImpairmentOptions = [
  { value: "Dementia", label: "Dementia" },
  { value: "Learning Disability", label: "Learning Disability" },
  { value: "Stroke", label: "Stroke" },
  { value: "Brain Injury", label: "Brain Injury" },
  { value: "Confusion", label: "Confusion" },
  { value: "Drowsiness", label: "Drowsiness" },
  { value: "Unconscious", label: "Unconscious" },
  { value: "Substance misuse", label: "Substance misuse" },
];

const MentalCapacitySection = ({ formData, handleChange }) => {
  // Memoized impairment mapper to prevent unnecessary recalculations
  const selectedImpairments = (formData.brain_impairment_types || []).map(
    (val) => ({ value: val, label: val })
  );

  const handleImpairmentChange = (selected) => {
    handleChange(
      "brain_impairment_types",
      selected ? selected.map((s) => s.value) : []
    );
  };

  const inputStyle = {
    "& .MuiFilledInput-root": {
      bgcolor: "#f8fafc",
      borderRadius: "14px",
      border: "1px solid #f1f5f9",
      transition: "0.2s",
      "&:hover": { bgcolor: "#f1f5f9" },
      "&.Mui-focused": { bgcolor: "#fff", borderColor: "warning.main" }
    },
    "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": { 
      borderBottom: "none !important" 
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 900, color: "#d97706", display: 'flex', alignItems: 'center', gap: 1.5, letterSpacing: 0.5 }}>
        <Box sx={{ p: 0.8, bgcolor: '#fef3c7', borderRadius: '8px', display: 'flex', color: '#d97706' }}>
          <Psychology sx={{ fontSize: 16 }} />
        </Box> 
        MENTAL CAPACITY ASSESSMENT (STAGE 1 & 2)
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <TextField
            fullWidth multiline rows={2} variant="filled"
            label="What concerns/triggers have given rise to this assessment?"
            value={formData.concerns_triggers || ""}
            onChange={(e) => handleChange("concerns_triggers", e.target.value)}
            sx={inputStyle}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth multiline rows={2} variant="filled"
            label="Details of actions taken to support individual's own decision-making"
            value={formData.actions_taken || ""}
            onChange={(e) => handleChange("actions_taken", e.target.value)}
            sx={inputStyle}
          />
        </Grid>

        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: "#fffbeb", borderRadius: "20px", border: "1px solid #fef3c7" }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} justifyContent="space-around">
              <FormControlLabel
                control={
                  <Switch 
                    checked={formData.decision_urgent || false} 
                    onChange={(e) => handleChange("decision_urgent", e.target.checked)} 
                    color="warning" 
                  />
                }
                label={<Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#92400e" }}>Can decision be delayed?</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch 
                    checked={formData.brain_impairment || false} 
                    onChange={(e) => handleChange("brain_impairment", e.target.checked)} 
                    color="warning" 
                  />
                }
                label={<Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#92400e" }}>Has brain/mind impairment?</Typography>}
              />
            </Stack>
          </Paper>
        </Grid>

        {formData.brain_impairment && (
          <Grid size={12}>
            <Box sx={{ p: 2.5, borderRadius: "16px", border: "1px solid #fed7aa", bgcolor: "#fffcf9", animation: 'fadeIn 0.3s ease-in-out' }}>
              <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 900, color: "#9a3412", display: 'block', textTransform: 'uppercase' }}>
                Impairment Classifications
              </Typography>
              <MultiSelect
                options={brainImpairmentOptions}
                value={selectedImpairments}
                onChange={handleImpairmentChange}
                placeholder="Select all that apply..."
              />
            </Box>
          </Grid>
        )}

        <Grid size={12}>
          <Typography variant="caption" sx={{ fontWeight: 900, color: "#64748b", mb: 2, display: 'block', letterSpacing: 1 }}>
            STAGE 2: THE FOUR FUNCTIONAL TESTS
          </Typography>
          <Stack spacing={2}>
            {[
              { key: "can_understand", label: "Can they understand the relevant information?", evidence: "understand_evidence" },
              { key: "can_retain", label: "Can they retain that information?", evidence: "retain_evidence" },
              { key: "can_weigh", label: "Can they use or weigh that information?", evidence: "weigh_evidence" },
              { key: "can_communicate", label: "Can they communicate their decision?", evidence: "communicate_evidence" },
              { key: "assessment_impaired", label: "Is this assessment impaired/distorted?", evidence: "impaired_evidence" },
            ].map((item) => {
              const isActive = formData[item.key];
              return (
                <Paper 
                  key={item.key} 
                  variant="outlined" 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: "18px", 
                    transition: '0.2s',
                    borderColor: isActive ? "warning.light" : "#e2e8f0", 
                    bgcolor: isActive ? "#fffbeb" : "#fff",
                    boxShadow: isActive ? '0 4px 12px rgba(245, 158, 11, 0.08)' : 'none'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={isActive || false} 
                        onChange={(e) => handleChange(item.key, e.target.checked)} 
                        color="warning" 
                        size="small" 
                      />
                    }
                    label={<Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: isActive ? "#92400e" : "#475569" }}>{item.label}</Typography>}
                  />
                  {isActive && (
                    <TextField
                      fullWidth multiline rows={2} variant="standard"
                      placeholder="Record the evidence for this functional test..."
                      value={formData[item.evidence] || ""}
                      onChange={(e) => handleChange(item.evidence, e.target.value)}
                      sx={{ 
                        mt: 1.5, 
                        "& .MuiInput-underline:before": { borderBottomColor: '#fde68a' },
                        "& .MuiInputBase-input": { fontSize: '0.875rem' }
                      }}
                      slotProps={{ input: { sx: { px: 1 } } }}
                    />
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth multiline rows={3} variant="filled"
            label="Additional Notes / Assessment Summary"
            value={formData.outcome_reason || ""}
            onChange={(e) => handleChange("outcome_reason", e.target.value)}
            sx={inputStyle}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MentalCapacitySection;